import { useState } from "react";
import { Loader2 } from "lucide-react";
import { RIM_COLOR, RIM_WIDTH } from "./Wheel";

type Props = {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  /** Color del botón (migration 015). Default amarillo del proyecto. */
  color?: string;
};

// Botón central de la ruleta — texto "GIRAR" siempre visible.
// La pantalla del kiosko es táctil (sin hover), por eso el texto va
// fijo, no oculto detrás de íconos. Color configurable desde admin
// para que pegue con la temática (rojo Mundial, pastel Pascua, etc.).
export function SpinButton({
  onClick,
  disabled,
  loading,
  label = "GIRAR",
  color = "#f7c948",
}: Props) {
  // Animación de "press + rebote" al click. State true mientras
  // corre el keyframe (~420ms); onAnimationEnd lo limpia. Si el user
  // re-clickea mientras está corriendo, reseteamos a false con un
  // micro-tick (rAF) para que el browser detecte el cambio de clase
  // y re-arranque la animación desde el frame 0.
  const [pressed, setPressed] = useState(false);

  function handleClick() {
    if (disabled || loading) return;
    if (pressed) {
      // Re-trigger limpio: pasamos a false, esperamos un frame, y
      // volvemos a true. Sin esto, agregar la misma clase mientras
      // la animación ya corre no re-arranca el keyframe.
      setPressed(false);
      requestAnimationFrame(() => setPressed(true));
    } else {
      setPressed(true);
    }
    onClick();
  }

  // Wrapper que centra (left/top 1/2 + translate negativo). El button
  // adentro NO usa translate — solo escala (sea con la animación de
  // press o con active:scale-95). Esto evita el bug: si pongo translate
  // y scale juntos en el button, el keyframe usa transform literal y
  // sobrescribe el translate de Tailwind, dejando el botón descentrado
  // mid-animación / al final. Con wrapper separado, no hay conflicto.
  return (
    <div
      className="pointer-events-none absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2"
    >
      <button
        onClick={handleClick}
        onAnimationEnd={(e) => {
          if (e.target === e.currentTarget) setPressed(false);
        }}
        disabled={disabled || loading}
        aria-label={label}
        className={`font-display pointer-events-auto flex h-24 w-24 items-center justify-center overflow-hidden rounded-full font-bold text-white shadow-2xl transition disabled:cursor-not-allowed ${
          pressed ? "animate-spin-press" : "active:scale-95"
        }`}
        style={{
          // Borde con el MISMO color/grosor que el rim del marco de la
          // ruleta (mantener en sync con Wheel.tsx RIM_*).
          border: `${RIM_WIDTH}px solid ${RIM_COLOR}`,
          // Color SÓLIDO 2D, sin gradient esfera. Antes había overlays
          // de highlight y sombra que daban efecto 3D; el user lo pidió
          // explícitamente plano "estética 2D con detalles esteticos
          // lindos sin agregar mucho ruido".
          background: color,
          // Glow externo difuso (semi-transparente porque es un halo,
          // no un círculo sólido).
          boxShadow: `0 0 30px ${color}99, 0 8px 20px rgba(0,0,0,0.4)`,
        }}
      >
        {loading ? (
          <Loader2 className="h-7 w-7 animate-spin" />
        ) : (
          <span className="text-lg leading-none">{label}</span>
        )}
      </button>
    </div>
  );
}
